import clsx from "clsx";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { AceStabilityResultView } from "./AceStabilityResultView";
import {
  requeueAceStabilityTestTask,
  type BackgroundAceStabilityState,
} from "./useAceStabilityTest";
import { formatElapsedMinutes } from "./aceStabilityResultHelpers";

export interface AceStabilityResultsPanelProps {
  state: BackgroundAceStabilityState | null;
  catalogueKey?: string | null;
  onDismiss: () => void;
  onViewHistory?: () => void;
  onRequeue?: () => void;
}

function formatElapsed(startedAt: number): string {
  return formatElapsedMinutes(startedAt);
}

function isQueuedState(state: BackgroundAceStabilityState): boolean {
  return (
    !state.result &&
    !state.error &&
    state.executionStartedAt == null &&
    (state.progress ?? 0) === 0
  );
}

export function AceStabilityResultsPanel({
  state,
  catalogueKey,
  onDismiss,
  onViewHistory,
  onRequeue,
}: AceStabilityResultsPanelProps) {
  const [requeuing, setRequeuing] = useState(false);
  if (!state) return null;

  const isRunning = !state.result && !state.error;
  const isQueued = isQueuedState(state);
  const isQueuedBehindAnother = Boolean(state.queuedBehindAnother);
  const isWaitingForWorker = Boolean(state.waitingForWorker) || (isQueued && !isQueuedBehindAnother);
  const isTrackingPaused = Boolean(state.trackingPaused);
  const elapsed = formatElapsed(state.startedAt);
  const showRequeue =
    isRunning && isWaitingForWorker && Date.now() - state.startedAt >= 60_000;

  async function handleRequeue() {
    if (requeuing) return;
    setRequeuing(true);
    try {
      await requeueAceStabilityTestTask(state.taskId);
      onRequeue?.();
    } finally {
      setRequeuing(false);
    }
  }

  const panelTitle = state.error
    ? "Ace stability test failed"
    : state.result
      ? state.result.stable
        ? `Stable — ${state.caseLabel}`
        : `Variance found — ${state.caseLabel}`
      : isTrackingPaused
        ? `Ace stability test still running — ${state.caseLabel}`
        : isQueuedBehindAnother
          ? `Ace stability test queued — ${state.caseLabel}`
          : isQueued
            ? `Ace stability test waiting to start — ${state.caseLabel}`
            : `Ace stability test — ${state.caseLabel}`;

  return (
    <div
      className={clsx(
        "mb-3 rounded-lg px-3 py-3 ring-1 text-sm",
        state.error
          ? "bg-rose-500/[0.08] ring-rose-500/25 text-rose-100"
          : state.result?.stable
            ? "bg-emerald-500/[0.08] ring-emerald-500/25 text-slate-200"
            : state.result
              ? "bg-amber-500/[0.08] ring-amber-500/25 text-slate-200"
              : isTrackingPaused
                ? "bg-amber-500/[0.08] ring-amber-500/25 text-slate-200"
                : "bg-indigo-500/[0.08] ring-indigo-500/25 text-slate-200",
      )}
      data-testid="ace-stability-results-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex items-center gap-2">
            {isRunning ? (
              <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin text-indigo-300" />
            ) : null}
            <p className="font-medium">{panelTitle}</p>
            {isRunning ? (
              <span className="text-xs text-slate-500 tabular-nums">({elapsed})</span>
            ) : null}
          </div>

          {state.error ? (
            <p className="text-xs text-rose-200/90">{state.error}</p>
          ) : isRunning ? (
            <>
              <p className="text-xs text-slate-400 leading-relaxed">
                {isQueuedBehindAnother
                  ? `Waiting for ${state.blockingTaskLabel ?? "another background job"} to finish. Only one Ace job runs at a time.`
                  : isWaitingForWorker
                    ? state.republishAttempted
                      ? "Re-queued — waiting for the worker to pick up this job. Cancelled jobs no longer block the queue."
                      : "Waiting for the worker to pick up this job. If this stays at 0% for more than a couple of minutes, use Re-queue."
                    : isTrackingPaused
                      ? "This is taking longer than expected. The job is still running — check Ops → Task Manager or open stability history for live status."
                      : "Running in the background — you can keep working in Agent Review."}
              </p>
              {showRequeue ? (
                <button
                  type="button"
                  onClick={() => void handleRequeue()}
                  disabled={requeuing}
                  className="text-xs text-indigo-300 hover:text-indigo-200 disabled:opacity-50"
                >
                  {requeuing ? "Re-queuing…" : "Re-queue job"}
                </button>
              ) : null}
              {!isWaitingForWorker && !isQueuedBehindAnother && !isTrackingPaused ? (
                <p className="text-xs text-slate-500 leading-relaxed">
                  LOA cases are slow: expect about 1–2 minutes per Ace run.
                </p>
              ) : null}
              <div className="flex justify-between gap-2 text-xs text-slate-400">
                <span className="truncate">{state.message}</span>
                {state.progress != null && state.progress > 0 ? (
                  <span className="tabular-nums shrink-0">{state.progress}%</span>
                ) : null}
              </div>
              {state.progress != null && state.progress > 0 ? (
                <div className="h-2 overflow-hidden rounded-full bg-slate-800">
                  <div
                    className="h-full bg-indigo-500 transition-all duration-300"
                    style={{ width: `${state.progress}%` }}
                  />
                </div>
              ) : null}
            </>
          ) : state.result ? (
            <AceStabilityResultView result={state.result} catalogueKey={catalogueKey} />
          ) : null}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          {onViewHistory ? (
            <button
              type="button"
              onClick={onViewHistory}
              className="text-xs text-indigo-300 hover:text-indigo-200"
            >
              History
            </button>
          ) : null}
          <button
            type="button"
            onClick={onDismiss}
            className="text-xs text-slate-400 hover:text-slate-200"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
